import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getUserByShortcode,
  convertFirebaseTimestamp,
  calculateEnhancedOptimalPace,
  calculateOptimalPace,
  calculateRefreshRate,
  mergeInsatiableJournalEntriesWithDoryRows,
  getFoundersFirstJournalsForGroupsAdminBatch,
  getAccountabilityForGroupsAdminBatch,
  saveAccountabilityGoals,
  computeAccountabilityGoalAccomplishment,
  formatAccountabilityCount,
  isAccountabilityGoalMet,
  formatAccountabilityPriorDate,
  buildInitialGoalDraft,
  parseAccountabilityBundle,
  entryTimestampToMillis,
  getCompletedCommitmentsPointsByUserId
} from '../../services/funnelService';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const GroupsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const GroupCard = styled.div`
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.sm};
  }
`;

const GroupName = styled.h3`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const GroupDetails = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
`;

// Overlay styles (similar to OptimalPaceSoloScreen)
const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.sm};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const OverlayContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${props => props.$compact ? theme.spacing.md : theme.spacing.xl};
  max-width: 95vw;
  width: 100%;
  max-height: 98vh;
  min-height: ${props => props.$compact ? 'auto' : '90vh'};
  overflow-y: auto;
  position: relative;
  box-shadow: ${theme.shadows.lg};
  display: flex;
  flex-direction: column;
  justify-content: ${props => props.$centered ? 'center' : 'flex-start'};
  align-items: center;
`;

const OverlayCloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: transparent;
  border: none;
  color: ${theme.colors.textLight};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.bold};
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: color ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  border-radius: 50%;
  
  &:hover {
    color: ${theme.colors.text};
    background: ${theme.colors.neutral}20;
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const OverlayIcon = styled.img`
  width: ${props => props.$small ? '88px' : '176px'};
  height: ${props => props.$small ? '90.5px' : '181px'};
  object-fit: contain;
  object-position: center;
  margin: ${props => props.$centered ? '0' : '70px'} auto ${theme.spacing.lg} auto;
  display: block;
  cursor: pointer;
  transition: width ${theme.animation.durations.normal} ${theme.animation.easings.easeOut},
              height ${theme.animation.durations.normal} ${theme.animation.easings.easeOut},
              transform ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const MastermindHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.doryGreen};
  text-align: center;
  margin: ${props => props.$smallTopMargin ? `calc(${theme.spacing.lg} / 2)` : theme.spacing.lg} 0 ${theme.spacing.md} 0;
  transition: margin-top ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const OverlayHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  text-align: center;
  margin: ${theme.spacing.lg} 0 ${theme.spacing.xl} 0;
`;

const TableTopBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${theme.spacing.md};
  width: 100%;
  flex-shrink: 0;
  margin-bottom: ${theme.spacing.sm};
  padding-right: ${theme.spacing.xl};
`;

const TableTopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  min-width: 0;
  justify-self: start;
`;

const TableTopBarCenter = styled.div`
  display: flex;
  justify-content: center;
  justify-self: center;
`;

const TableTopBarSpacer = styled.div`
  justify-self: end;
`;

const TableTopBarIcon = styled.img`
  width: 44px;
  height: 45px;
  object-fit: contain;
  flex-shrink: 0;
`;

const TableGroupName = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.doryGreen};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`;

const MembersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  align-items: center;
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  align-items: center;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: ${theme.spacing.md} 0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  margin-bottom: ${theme.spacing.md};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  width: fit-content;
`;

const HeaderName = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  min-width: 120px;
  width: 120px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-left: ${theme.spacing.md};
`;

const HeaderData = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  flex-wrap: nowrap;
  justify-content: center;
`;

const HeaderItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  align-items: center;
  min-width: 100px;
  flex-shrink: 0;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: ${theme.spacing.md} 0;
  background: ${props => {
    if (props.$isTopRow) {
      return 'rgba(67, 95, 96, 0.15)'; // Lighter Dory green (#435F60 with 15% opacity)
    }
    return props.$isEven ? 'transparent' : 'rgba(0, 0, 0, 0.02)';
  }};
`;

const MemberContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  width: fit-content;
`;

const MemberInitials = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  min-width: 120px;
  width: 120px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-left: ${theme.spacing.md};
`;

const MemberData = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  flex-wrap: nowrap;
  justify-content: center;
`;

const DataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  align-items: center;
  justify-content: center;
  min-width: 100px;
  flex-shrink: 0;
`;

const DataLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
`;

const DataValue = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.text};
  text-align: center;
  padding: calc(${theme.spacing.xs} / 2) 0;
`;

const PushScoreValue = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  padding: calc(${theme.spacing.xs} / 2) 0;
`;

const PushScoreLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.textLight};
  text-align: center;
`;

const PeakDayCheck = styled.span`
  color: ${theme.colors.doryGreen};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const AccountabilityTableScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  min-height: 0;
  width: 100%;
  align-items: center;
`;

const AccountabilityUserPicker = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-bottom: ${theme.spacing.md};
  flex-shrink: 0;
`;

const AccountabilityUserSelect = styled.select`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  min-width: 220px;
  max-width: 360px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const AccountabilityUserLabel = styled.label`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.textLight};
`;

const GoalStepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(${theme.spacing.xs} / 2);
`;

const GoalStepperButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.sm};
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  padding: 0;
  line-height: 1;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const GoalStepperValue = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  min-width: 28px;
  text-align: center;
  color: ${theme.colors.text};
`;

const OpenItemsCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc(${theme.spacing.xs} / 2);
  min-width: 180px;
  max-width: 220px;
`;

const OpenItemInput = styled.input`
  width: 100%;
  padding: calc(${theme.spacing.xs} / 2) ${theme.spacing.xs};
  border: 1px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const SmallButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: calc(${theme.spacing.xs} / 2) ${theme.spacing.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const GoalStatusDetail = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
`;

const OpenItemsStatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 140px;
  max-width: 180px;
`;

const OpenItemStatus = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${props => props.$done ? theme.colors.doryGreen : theme.colors.textLight};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;

  &::before {
    content: '${props => props.$done ? '✓' : '○'} ';
  }
`;

const OpenItemStatusButton = styled.button`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &::before {
    content: '○ ';
  }

  &:hover:not(:disabled) {
    color: ${theme.colors.doryGreen};
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const AccountabilityCountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(${theme.spacing.xs} / 2);
`;

const PushScoreEquation = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  margin-top: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.sm} 0;
`;

const ViewToggleRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.xs};
  margin-bottom: ${props => props.$compact ? 0 : theme.spacing.lg};
  width: ${props => props.$compact ? 'auto' : '100%'};
  flex-shrink: 0;
`;

const ViewToggleButton = styled.button`
  background: ${props => props.$active ? theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.text};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  padding: ${props => props.$compact
    ? `calc(${theme.spacing.xs} / 2) ${theme.spacing.md}`
    : `${theme.spacing.sm} ${theme.spacing.lg}`};
  font-family: ${theme.typography.bodyFont};
  font-size: ${props => props.$compact ? theme.typography.sizes.small : theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primary};
    color: white;
  }
`;

/** Calendar date in an IANA zone, as YYYY/MM/DD for founders-first commitments. */
const formatDateYyyyMmDdSlashedInTimeZone = (date, ianaTimeZone) => {
  const tz =
    ianaTimeZone && typeof ianaTimeZone === 'string' && ianaTimeZone.trim()
      ? ianaTimeZone.trim()
      : 'UTC';
  const d = date instanceof Date ? date : new Date(date);
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(d);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (y && m && day) {
      return `${y}/${m}/${day}`;
    }
  } catch {
    // fall through
  }
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
};

const formatTodayYyyyMmDdSlashedInTimeZone = (ianaTimeZone) =>
  formatDateYyyyMmDdSlashedInTimeZone(new Date(), ianaTimeZone);

const buildMemberDisplayName = (shortcode, ffBundle, insatiableName) => {
  const doryUserId = ffBundle?.doryUserId && String(ffBundle.doryUserId).trim();
  const ffFirst = (ffBundle?.doryFirstName && String(ffBundle.doryFirstName).trim()) || '';
  const ffLast = (ffBundle?.doryLastName && String(ffBundle.doryLastName).trim()) || '';
  const ffFullName = (ffBundle?.doryName && String(ffBundle.doryName).trim()) || '';

  const formatDisplayName = (first, last, fullName) => {
    if (first) {
      const lastInitial = last ? last.charAt(0).toUpperCase() : '';
      return {
        name: [first, last].filter(Boolean).join(' '),
        firstName: first,
        lastName: last,
        displayName: `${first} ${lastInitial}`.trim()
      };
    }
    if (fullName) {
      const nameParts = fullName.split(' ').filter((part) => part.length > 0);
      const first = nameParts[0] || '';
      const last = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      const lastInitial = last ? last.charAt(0).toUpperCase() : '';
      return {
        name: fullName,
        firstName: first,
        lastName: last,
        displayName: `${first} ${lastInitial}`.trim()
      };
    }
    return null;
  };

  if (doryUserId) {
    const fromFoundersFirst = formatDisplayName(ffFirst, ffLast, ffFullName);
    if (fromFoundersFirst?.displayName) {
      return fromFoundersFirst;
    }
  }

  const fromInsatiable = formatDisplayName(
    insatiableName.trim().split(' ').filter(Boolean)[0] || '',
    insatiableName.trim().split(' ').filter(Boolean).length > 1
      ? insatiableName.trim().split(' ').filter(Boolean).slice(-1)[0]
      : '',
    insatiableName.trim()
  );

  if (fromInsatiable?.displayName) {
    return fromInsatiable;
  }

  return {
    name: null,
    firstName: null,
    lastName: null,
    displayName: shortcode.substring(0, 2).toUpperCase()
  };
};

const getMemberFullName = (member) => {
  if (!member) return '';
  if (member.name && typeof member.name === 'string' && member.name.trim()) {
    return member.name.trim();
  }
  if (member.initials && typeof member.initials === 'string' && member.initials.trim()) {
    return member.initials.trim();
  }
  return member.shortcode || '';
};

const getMemberLatestAccountabilityDate = (member) => {
  const todayDate =
    member?.todayAccountabilityGoals?.date &&
    typeof member.todayAccountabilityGoals.date === 'string'
      ? member.todayAccountabilityGoals.date
      : '';
  const priorDate =
    member?.accountabilityGoals?.date && typeof member.accountabilityGoals.date === 'string'
      ? member.accountabilityGoals.date
      : '';
  if (todayDate && priorDate) {
    return todayDate > priorDate ? todayDate : priorDate;
  }
  return todayDate || priorDate || '';
};

const pickDefaultAccountabilityShortcode = (members) => {
  if (!members?.length) return null;
  let bestShortcode = null;
  let bestDate = '';
  for (const member of members) {
    const date = getMemberLatestAccountabilityDate(member);
    if (date && date > bestDate) {
      bestDate = date;
      bestShortcode = member.shortcode;
    }
  }
  if (bestShortcode) return bestShortcode;
  const sorted = [...members].sort((a, b) =>
    getMemberFullName(a).localeCompare(getMemberFullName(b))
  );
  return sorted[0]?.shortcode ?? null;
};

const GroupsScreen = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [membersData, setMembersData] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [tableView, setTableView] = useState('pace');
  const [selectedAccountabilityShortcode, setSelectedAccountabilityShortcode] = useState(null);
  const [goalDrafts, setGoalDrafts] = useState({});
  const [savingGoals, setSavingGoals] = useState({});
  const [togglingOpenItems, setTogglingOpenItems] = useState({});
  const dirtyGoalDraftsRef = useRef(new Set());

  const markGoalDraftDirty = (shortcode) => {
    dirtyGoalDraftsRef.current.add(shortcode);
  };

  useEffect(() => {
    loadGroups();
  }, []);

  // Poll for new journal entries while overlay is open
  useEffect(() => {
    if (!selectedGroup) {
      return;
    }

    // Poll every 10 seconds for new check-ins
    const pollInterval = setInterval(async () => {
      console.log('Polling for new journal entries...');
      const updatedMembers = await loadMembersData(selectedGroup);
      
      // Always update the members list to ensure Push Scores are recalculated and sorting is maintained
      // This will reorder rows if Push Scores have changed
      setMembersData(updatedMembers);
    }, 10000); // Poll every 10 seconds

    // Clean up interval when overlay closes
    return () => {
      clearInterval(pollInterval);
    };
  }, [selectedGroup]);

  useEffect(() => {
    setGoalDrafts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const member of membersData) {
        if (dirtyGoalDraftsRef.current.has(member.shortcode)) continue;
        next[member.shortcode] = buildInitialGoalDraft(member);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [membersData]);

  useEffect(() => {
    setSelectedAccountabilityShortcode((current) => {
      if (current && membersData.some((member) => member.shortcode === current)) {
        return current;
      }
      return pickDefaultAccountabilityShortcode(membersData);
    });
  }, [membersData]);

  const loadGroups = async () => {
    setLoading(true);
    try {
      console.log('Attempting to load groups from insatiable database...');
      const groupsRef = collection(db, 'groups');
      console.log('Groups collection reference created');
      
      const groupsSnapshot = await getDocs(groupsRef);
      console.log(`Query completed. Found ${groupsSnapshot.size} documents in groups collection`);
      
      if (groupsSnapshot.empty) {
        console.warn('Groups collection is empty or no documents found');
      }
      
      const groupsList = [];
      groupsSnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        console.log(`Processing group document "${docSnapshot.id}":`, data);
        console.log('Document data keys:', Object.keys(data));
        
        // Find the array field containing shortcodes
        // Check common field names first, then find first array field
        let members = null;
        if (data.members && Array.isArray(data.members)) {
          members = data.members;
          console.log(`Found members array with ${members.length} items`);
        } else if (data.shortcodes && Array.isArray(data.shortcodes)) {
          members = data.shortcodes;
          console.log(`Found shortcodes array with ${members.length} items`);
        } else {
          // Find the first array field in the document
          for (const key in data) {
            if (Array.isArray(data[key])) {
              members = data[key];
              console.log(`Found array field "${key}" with ${data[key].length} items`);
              break;
            }
          }
        }
        
        if (!members) {
          console.warn(`No array field found in group "${docSnapshot.id}". Available fields:`, Object.keys(data));
        }
        
        groupsList.push({
          id: docSnapshot.id,
          name: data.name || docSnapshot.id,
          members: members || []
        });
      });
      
      console.log(`Successfully loaded ${groupsList.length} groups:`, groupsList);
      setGroups(groupsList);
    } catch (error) {
      console.error('Error loading groups:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      // Set empty array on error so UI shows "No groups found" instead of loading forever
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const isToday = (timestamp) => {
    if (!timestamp) return false;
    const entryDate = convertFirebaseTimestamp(timestamp);
    if (!entryDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const entryDateOnly = new Date(entryDate);
    entryDateOnly.setHours(0, 0, 0, 0);
    
    return entryDateOnly.getTime() === today.getTime();
  };

  const getTodayCheckIn = (journalEntries) => {
    if (!journalEntries || journalEntries.length === 0) return null;
    
    // Find the most recent entry from today
    const todayEntries = journalEntries
      .filter(entry => isToday(entry.timestamp))
      .sort((a, b) => {
        const aTime = convertFirebaseTimestamp(a.timestamp) || a.timestamp;
        const bTime = convertFirebaseTimestamp(b.timestamp) || b.timestamp;
        return new Date(bTime) - new Date(aTime);
      });
    
    return todayEntries.length > 0 ? todayEntries[0] : null;
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}`;
  };

  const fetchMemberData = async (shortcode, ffBundle = {}, accountabilityBundle = null) => {
    const priorDoc = accountabilityBundle?.prior ?? null;
    const todayDoc = accountabilityBundle?.today ?? null;
    try {
      const userData = await getUserByShortcode(shortcode);
      if (!userData) {
        return {
          shortcode,
          name: null,
          firstName: null,
          lastName: null,
          initials: '??',
          checkIn: null,
          hasTodayCheckIn: false,
          doryUserId: null,
          doryTimeZone: null,
          accountabilityGoals: null,
          todayAccountabilityGoals: null,
          goalAccomplishment: null
        };
      }

      const doryRows = ffBundle.entries || [];
      const mergedJournalEntries = mergeInsatiableJournalEntriesWithDoryRows(
        userData.journalEntries || [],
        doryRows
      );

      const todayCheckIn = getTodayCheckIn(mergedJournalEntries);

      let optimalPace = null;
      if (todayCheckIn && todayCheckIn.emotionalStates && todayCheckIn.hoursWorked) {
        const entriesForOptimal = mergedJournalEntries
          .filter(entry => entry.timestamp && entry.hoursWorked)
          .sort((a, b) => {
            const aTime = convertFirebaseTimestamp(a.timestamp) || a.timestamp;
            const bTime = convertFirebaseTimestamp(b.timestamp) || b.timestamp;
            return new Date(aTime) - new Date(bTime);
          })
          .map(entry => ({
            ...entry,
            timestamp: convertFirebaseTimestamp(entry.timestamp) || entry.timestamp
          }));

        const targetMs = entryTimestampToMillis(todayCheckIn.timestamp);
        let op = null;
        if (entriesForOptimal.length > 0 && targetMs != null) {
          let targetIndex = -1;
          for (let i = entriesForOptimal.length - 1; i >= 0; i--) {
            if (entryTimestampToMillis(entriesForOptimal[i].timestamp) === targetMs) {
              targetIndex = i;
              break;
            }
          }
          if (targetIndex >= 0) {
            op = calculateEnhancedOptimalPace(entriesForOptimal, targetIndex);
          }
          if (!op) {
            op = calculateEnhancedOptimalPace(entriesForOptimal);
          }
        }
        if (!op) {
          op = calculateOptimalPace(todayCheckIn);
        }
        if (op && op.optimalHours !== undefined && op.optimalHours !== null) {
          optimalPace = Math.round(op.optimalHours);
        } else if (userData.calculatedMetrics?.optimalPace?.optimalHours !== undefined) {
          optimalPace = Math.round(userData.calculatedMetrics.optimalPace.optimalHours);
        }
      }

      let refreshRate = null;
      const rrComputed = calculateRefreshRate(mergedJournalEntries);
      if (rrComputed !== null && rrComputed !== undefined) {
        refreshRate = rrComputed;
      } else if (userData.calculatedMetrics?.refreshRate !== null && userData.calculatedMetrics?.refreshRate !== undefined) {
        refreshRate = userData.calculatedMetrics.refreshRate;
      }

      const doryUserId =
        ffBundle.doryUserId && String(ffBundle.doryUserId).trim()
          ? String(ffBundle.doryUserId).trim()
          : null;
      const doryTimeZone = ffBundle.doryTimeZone || null;

      if (!doryUserId && userData.appUserId) {
        console.warn(
          `Ignoring stale appUserId for shortcode ${shortcode}: ${userData.appUserId} (no founders-first shortCode match)`
        );
      }

      let pointsNeededForDay = null;
      if (
        todayCheckIn &&
        todayCheckIn.pointsPlan !== null &&
        todayCheckIn.pointsPlan !== undefined
      ) {
        const n = Number(todayCheckIn.pointsPlan);
        if (!Number.isNaN(n)) {
          pointsNeededForDay = n;
        }
      }

      let peakDayCommitmentPoints = null;
      let isPeakPerformanceDay = false;
      if (todayCheckIn && doryUserId) {
        const todayStr = formatTodayYyyyMmDdSlashedInTimeZone(doryTimeZone);
        peakDayCommitmentPoints = await getCompletedCommitmentsPointsByUserId(
          doryUserId,
          todayStr,
          doryTimeZone || 'UTC'
        );
        if (pointsNeededForDay !== null) {
          isPeakPerformanceDay = peakDayCommitmentPoints >= pointsNeededForDay;
        }
      }

      const goalAccomplishment = await computeAccountabilityGoalAccomplishment(
        priorDoc,
        mergedJournalEntries,
        doryUserId,
        doryTimeZone
      );

      const insatiableName = userData.userInfo?.name || '';
      const { name, firstName, lastName, displayName } = buildMemberDisplayName(
        shortcode,
        ffBundle,
        insatiableName
      );

      if (
        doryUserId &&
        insatiableName.trim() &&
        name &&
        insatiableName.trim().toLowerCase() !== name.trim().toLowerCase()
      ) {
        console.warn(
          `Name mismatch for shortcode ${shortcode}: Insatiable="${insatiableName}" vs founders-first="${name}" (doryUserId=${doryUserId})`
        );
      }

      console.log(`Member ${shortcode}: displayName="${displayName}", doryUserId="${doryUserId || ''}"`);

      // Calculate Push Score: (HoursPlan - Optimal Pace) * Pressure Score * (100 - Refresh Rate)
      let pushScore = null;
      if (todayCheckIn && todayCheckIn.hoursPlan !== null && todayCheckIn.hoursPlan !== undefined &&
          optimalPace !== null && optimalPace !== undefined &&
          todayCheckIn.pressureScore !== null && todayCheckIn.pressureScore !== undefined &&
          refreshRate !== null && refreshRate !== undefined) {
        pushScore = (todayCheckIn.hoursPlan - optimalPace) * todayCheckIn.pressureScore * (100 - refreshRate);
      }

      return {
        shortcode,
        name: name || null,
        firstName,
        lastName,
        initials: displayName,
        doryUserId,
        doryTimeZone,
        accountabilityGoals: priorDoc,
        todayAccountabilityGoals: todayDoc,
        goalAccomplishment,
        hasTodayCheckIn: !!todayCheckIn,
        checkIn: todayCheckIn ? {
          pressureScore: todayCheckIn.pressureScore,
          optimalPace,
          hoursPlan: todayCheckIn.hoursPlan,
          pointsNeededForDay,
          peakDayCommitmentPoints,
          isPeakPerformanceDay,
          pushScore,
          checkInsLast7Days: goalAccomplishment.actualCheckIns,
          peakDaysLast7Days: goalAccomplishment.actualPeakDays
        } : {
          checkInsLast7Days: goalAccomplishment.actualCheckIns,
          peakDaysLast7Days: goalAccomplishment.actualPeakDays
        }
      };
    } catch (error) {
      console.error(`Error fetching user ${shortcode}:`, error);
      return {
        shortcode,
        name: null,
        firstName: null,
        lastName: null,
        initials: '??',
        checkIn: null,
        hasTodayCheckIn: false,
        doryUserId: null,
        doryTimeZone: null,
        accountabilityGoals: null,
        todayAccountabilityGoals: null,
        goalAccomplishment: null
      };
    }
  };

  const loadMembersData = async (group) => {
    try {
      const memberShortcodes = group.members || [];
      let ffByShortcode = {};
      try {
        ffByShortcode = await getFoundersFirstJournalsForGroupsAdminBatch(memberShortcodes);
      } catch (ffError) {
        console.error('Founders-first journals batch failed:', ffError);
      }

      const userIds = memberShortcodes
        .map((shortcode) => ffByShortcode[shortcode]?.doryUserId)
        .filter(Boolean);

      let accountabilityByUserId = {};
      try {
        accountabilityByUserId = await getAccountabilityForGroupsAdminBatch(userIds);
      } catch (accError) {
        console.error('Accountability batch failed:', accError);
      }

      const membersPromises = memberShortcodes.map((shortcode) => {
        const bundle = ffByShortcode[shortcode] || { entries: [] };
        const doryUserId = bundle.doryUserId || null;
        const accountabilityBundle = parseAccountabilityBundle(
          doryUserId ? accountabilityByUserId[doryUserId] : null
        );
        return fetchMemberData(shortcode, bundle, accountabilityBundle);
      });
      const members = await Promise.all(membersPromises);
      return members.sort((a, b) => (a.initials || '').localeCompare(b.initials || ''));
    } catch (error) {
      console.error('Error loading members:', error);
      return [];
    }
  };

  const handleGroupClick = async (group) => {
    setSelectedGroup(group);
    setLoadingMembers(true);
    setMembersData([]);
    setShowTable(false);
    setGoalDrafts({});
    dirtyGoalDraftsRef.current.clear();

    const initialMembers = await loadMembersData(group);
    setMembersData(initialMembers);
    setLoadingMembers(false);
  };

  const handleIconClick = () => {
    setShowTable(true);
  };

  const handleCloseOverlay = () => {
    setSelectedGroup(null);
    setMembersData([]);
    setShowTable(false);
    setTableView('pace');
    setSelectedAccountabilityShortcode(null);
    setGoalDrafts({});
    setSavingGoals({});
    setTogglingOpenItems({});
    dirtyGoalDraftsRef.current.clear();
  };

  const handleMarkOpenItemComplete = async (member, label) => {
    const priorGoals = member.accountabilityGoals;
    if (!member.doryUserId || !priorGoals?.date || !label) return;

    const toggleKey = `${member.shortcode}:${label}`;
    setTogglingOpenItems((prev) => ({ ...prev, [toggleKey]: true }));
    try {
      const priorOpenItems = priorGoals.openItems ?? priorGoals['open items'] ?? {};
      await saveAccountabilityGoals(member.doryUserId, {
        cis: priorGoals.cis,
        pds: priorGoals.pds,
        openItems: { ...priorOpenItems, [label]: true },
        date: priorGoals.date,
        timeZone: member.doryTimeZone
      });

      if (selectedGroup) {
        const updatedMembers = await loadMembersData(selectedGroup);
        setMembersData(updatedMembers);
      }
    } catch (error) {
      console.error('Failed to mark open item complete:', error);
      window.alert('Failed to mark open item complete. Please try again.');
    } finally {
      setTogglingOpenItems((prev) => {
        const next = { ...prev };
        delete next[toggleKey];
        return next;
      });
    }
  };

  const adjustGoalDraft = (shortcode, field, delta) => {
    markGoalDraftDirty(shortcode);
    setGoalDrafts((prev) => {
      const draft = prev[shortcode] || { cis: 0, pds: 0, openItems: [''] };
      const current = Number(draft[field]) || 0;
      return {
        ...prev,
        [shortcode]: {
          ...draft,
          [field]: Math.max(0, current + delta)
        }
      };
    });
  };

  const renderGoalStepper = (member, field, draft) => (
    <GoalStepper>
      <GoalStepperButton
        type="button"
        disabled={!member.doryUserId}
        onClick={() => adjustGoalDraft(member.shortcode, field, -1)}
        aria-label={`Decrease ${field} goal`}
      >
        −
      </GoalStepperButton>
      <GoalStepperValue>{Number(draft[field]) || 0}</GoalStepperValue>
      <GoalStepperButton
        type="button"
        disabled={!member.doryUserId}
        onClick={() => adjustGoalDraft(member.shortcode, field, 1)}
        aria-label={`Increase ${field} goal`}
      >
        +
      </GoalStepperButton>
    </GoalStepper>
  );

  const updateOpenItemDraft = (shortcode, index, value) => {
    markGoalDraftDirty(shortcode);
    setGoalDrafts((prev) => {
      const draft = prev[shortcode] || { cis: 0, pds: 0, openItems: [''] };
      const items = [...(draft.openItems || [''])];
      items[index] = value;
      if (index === items.length - 1 && value.trim()) {
        items.push('');
      }
      return {
        ...prev,
        [shortcode]: { ...draft, openItems: items }
      };
    });
  };

  const handleSaveGoals = async (member) => {
    if (!member.doryUserId) return;
    const draft = goalDrafts[member.shortcode];
    if (!draft) return;

    setSavingGoals((prev) => ({ ...prev, [member.shortcode]: true }));
    try {
      const openItemLabels = (draft.openItems || [])
        .map((s) => (typeof s === 'string' ? s.trim() : ''))
        .filter(Boolean);
      const existingStatuses =
        member.todayAccountabilityGoals?.openItems ||
        member.accountabilityGoals?.openItems ||
        {};
      const openItemsMap = {};
      for (const label of openItemLabels) {
        openItemsMap[label] = existingStatuses[label] ?? false;
      }

      await saveAccountabilityGoals(member.doryUserId, {
        cis: draft.cis,
        pds: draft.pds,
        openItems: openItemsMap,
        date: member.todayAccountabilityGoals?.date,
        timeZone: member.doryTimeZone
      });

      if (selectedGroup) {
        const updatedMembers = await loadMembersData(selectedGroup);
        setMembersData(updatedMembers);
        const updatedMember = updatedMembers.find((m) => m.shortcode === member.shortcode);
        if (updatedMember) {
          dirtyGoalDraftsRef.current.delete(member.shortcode);
          setGoalDrafts((prev) => ({
            ...prev,
            [member.shortcode]: buildInitialGoalDraft(updatedMember)
          }));
        }
      }
    } catch (error) {
      console.error('Failed to save accountability goals:', error);
      window.alert('Failed to save accountability goals. Please try again.');
    } finally {
      setSavingGoals((prev) => ({ ...prev, [member.shortcode]: false }));
    }
  };

  const accountabilityMemberOptions = useMemo(() => {
    return [...membersData].sort((a, b) =>
      getMemberFullName(a).localeCompare(getMemberFullName(b))
    );
  }, [membersData]);

  const displayedMembers = useMemo(() => {
    if (tableView === 'pace') {
      return [...membersData]
        .filter((member) => member.hasTodayCheckIn)
        .sort((a, b) => {
          const scoreA = a.checkIn?.pushScore ?? -Infinity;
          const scoreB = b.checkIn?.pushScore ?? -Infinity;
          return scoreB - scoreA;
        });
    }
    if (tableView === 'accountability') {
      if (!selectedAccountabilityShortcode) return [];
      const selected = membersData.find(
        (member) => member.shortcode === selectedAccountabilityShortcode
      );
      return selected ? [selected] : [];
    }
    return membersData;
  }, [membersData, tableView, selectedAccountabilityShortcode]);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading groups...</LoadingMessage>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <SectionHeader>
          <SectionTitle>Groups ({groups.length})</SectionTitle>
        </SectionHeader>
        
        {groups.length === 0 ? (
          <LoadingMessage>No groups found.</LoadingMessage>
        ) : (
          <GroupsList>
            {groups.map((group) => (
              <GroupCard key={group.id} onClick={() => handleGroupClick(group)}>
                <GroupName>{group.name}</GroupName>
                <GroupDetails>
                  {group.members?.length || 0} member{group.members?.length !== 1 ? 's' : ''}
                </GroupDetails>
              </GroupCard>
            ))}
          </GroupsList>
        )}
      </Container>

      {selectedGroup && (
        <OverlayContainer onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseOverlay();
          }
        }}>
          <OverlayContent onClick={(e) => e.stopPropagation()} $centered={!showTable} $compact={showTable}>
            <OverlayCloseButton onClick={handleCloseOverlay}>×</OverlayCloseButton>
            {!showTable ? (
              <>
                <OverlayIcon
                  src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/screen%204%20puzzle.svg?alt=media&token=d2872707-035b-49df-9cdc-b36fe385b394"
                  alt="Group icon"
                  onClick={handleIconClick}
                  $centered
                />
                <MastermindHeading>Mastermind</MastermindHeading>
                <OverlayHeading>Is anyone on the edge of burning out today?</OverlayHeading>
              </>
            ) : (
              <>
                <TableTopBar>
                  <TableTopBarLeft>
                    <TableTopBarIcon
                      src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/screen%204%20puzzle.svg?alt=media&token=d2872707-035b-49df-9cdc-b36fe385b394"
                      alt="Group icon"
                    />
                    <TableGroupName>{selectedGroup.name}</TableGroupName>
                  </TableTopBarLeft>
                  <TableTopBarCenter>
                    <ViewToggleRow $compact>
                      <ViewToggleButton
                        type="button"
                        $active={tableView === 'pace'}
                        $compact
                        onClick={() => setTableView('pace')}
                      >
                        Pace
                      </ViewToggleButton>
                      <ViewToggleButton
                        type="button"
                        $active={tableView === 'accountability'}
                        $compact
                        onClick={() => setTableView('accountability')}
                      >
                        Accountability
                      </ViewToggleButton>
                    </ViewToggleRow>
                  </TableTopBarCenter>
                  <TableTopBarSpacer aria-hidden="true" />
                </TableTopBar>
                {loadingMembers ? (
                  <LoadingMessage>Loading members...</LoadingMessage>
                ) : (
                  <AccountabilityTableScroll>
                {tableView === 'accountability' && accountabilityMemberOptions.length > 0 && (
                  <AccountabilityUserPicker>
                    <AccountabilityUserLabel htmlFor="accountability-user-select">
                      Member
                    </AccountabilityUserLabel>
                    <AccountabilityUserSelect
                      id="accountability-user-select"
                      value={selectedAccountabilityShortcode || ''}
                      onChange={(e) => setSelectedAccountabilityShortcode(e.target.value || null)}
                    >
                      {accountabilityMemberOptions.map((member) => (
                        <option key={member.shortcode} value={member.shortcode}>
                          {getMemberFullName(member)}
                        </option>
                      ))}
                    </AccountabilityUserSelect>
                  </AccountabilityUserPicker>
                )}
                {displayedMembers.length > 0 && (
                  <HeaderRow>
                    <HeaderContent>
                      <HeaderName></HeaderName>
                      <HeaderData>
                        {tableView === 'pace' ? (
                          <>
                            <HeaderItem>
                              <DataLabel>Pressure Score</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>Optimal Pace</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>Hours Plan</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>Points Needed</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>Peak Day</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <PushScoreLabel>Push Score</PushScoreLabel>
                            </HeaderItem>
                          </>
                        ) : (
                          <>
                            <HeaderItem>
                              <DataLabel>Prior Date</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>Check-ins</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>Peak Days</DataLabel>
                            </HeaderItem>
                            <HeaderItem style={{ minWidth: '160px' }}>
                              <DataLabel>Open Items Met</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>CIS Goal</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel>PDS Goal</DataLabel>
                            </HeaderItem>
                            <HeaderItem style={{ minWidth: '200px' }}>
                              <DataLabel>Open Items</DataLabel>
                            </HeaderItem>
                            <HeaderItem>
                              <DataLabel></DataLabel>
                            </HeaderItem>
                          </>
                        )}
                      </HeaderData>
                    </HeaderContent>
                  </HeaderRow>
                )}
                <MembersList>
                {displayedMembers.map((member, index) => {
                  const draft = goalDrafts[member.shortcode] || { cis: 0, pds: 0, openItems: [''] };
                  const accomplishment = member.goalAccomplishment;
                  return (
                  <MemberRow key={`${member.shortcode}-${index}`} $isEven={index % 2 === 0} $isTopRow={index === 0 && tableView === 'pace'}>
                    <MemberContent>
                      <MemberInitials>{member.initials || member.shortcode.substring(0, 2).toUpperCase()}</MemberInitials>
                      <MemberData>
                      {tableView === 'pace' ? (
                        <>
                          <DataItem>
                            <DataValue>
                              {member.checkIn?.pressureScore !== null && member.checkIn?.pressureScore !== undefined
                                ? member.checkIn.pressureScore
                                : ''}
                            </DataValue>
                          </DataItem>
                          <DataItem>
                            <DataValue>
                              {member.checkIn?.optimalPace !== null && member.checkIn?.optimalPace !== undefined
                                ? member.checkIn.optimalPace
                                : ''}
                            </DataValue>
                          </DataItem>
                          <DataItem>
                            <DataValue>
                              {member.checkIn?.hoursPlan !== null && member.checkIn?.hoursPlan !== undefined
                                ? member.checkIn.hoursPlan
                                : ''}
                            </DataValue>
                          </DataItem>
                          <DataItem>
                            <DataValue>
                              {member.checkIn?.pointsNeededForDay !== null && member.checkIn?.pointsNeededForDay !== undefined
                                ? member.checkIn.pointsNeededForDay
                                : ''}
                            </DataValue>
                          </DataItem>
                          <DataItem>
                            {member.checkIn?.isPeakPerformanceDay ? (
                              <PeakDayCheck title="Peak performance day">✓</PeakDayCheck>
                            ) : (
                              <DataValue>
                                {member.checkIn?.peakDayCommitmentPoints != null
                                  ? member.checkIn.peakDayCommitmentPoints
                                  : ''}
                              </DataValue>
                            )}
                          </DataItem>
                          <DataItem>
                            <PushScoreValue>
                              {member.checkIn?.pushScore !== null && member.checkIn?.pushScore !== undefined
                                ? Math.round(member.checkIn.pushScore)
                                : ''}
                            </PushScoreValue>
                          </DataItem>
                        </>
                      ) : (
                        <>
                          <DataItem>
                            <DataValue>
                              {formatAccountabilityPriorDate(member.accountabilityGoals?.date)}
                            </DataValue>
                          </DataItem>
                          <DataItem>
                            <AccountabilityCountRow>
                              <DataValue>
                                {formatAccountabilityCount(
                                  accomplishment?.actualCheckIns,
                                  accomplishment?.cisGoal
                                )}
                              </DataValue>
                              {isAccountabilityGoalMet(
                                accomplishment?.actualCheckIns,
                                accomplishment?.cisGoal
                              ) && (
                                <PeakDayCheck title="Check-in goal met">✓</PeakDayCheck>
                              )}
                            </AccountabilityCountRow>
                          </DataItem>
                          <DataItem>
                            <AccountabilityCountRow>
                              <DataValue>
                                {formatAccountabilityCount(
                                  accomplishment?.actualPeakDays,
                                  accomplishment?.pdsGoal
                                )}
                              </DataValue>
                              {isAccountabilityGoalMet(
                                accomplishment?.actualPeakDays,
                                accomplishment?.pdsGoal
                              ) && (
                                <PeakDayCheck title="Peak day goal met">✓</PeakDayCheck>
                              )}
                            </AccountabilityCountRow>
                          </DataItem>
                          <DataItem>
                            {accomplishment?.openItemsTotal > 0 ? (
                              <OpenItemsStatusList>
                                {Object.entries(accomplishment.openItems).map(([label, done]) => {
                                  const toggleKey = `${member.shortcode}:${label}`;
                                  const isToggling = togglingOpenItems[toggleKey];
                                  if (done) {
                                    return (
                                      <OpenItemStatus key={label} $done title={label}>
                                        {label}
                                      </OpenItemStatus>
                                    );
                                  }
                                  return (
                                    <OpenItemStatusButton
                                      key={label}
                                      type="button"
                                      disabled={!member.doryUserId || isToggling}
                                      title={`Mark complete: ${label}`}
                                      onClick={() => handleMarkOpenItemComplete(member, label)}
                                    >
                                      {label}
                                    </OpenItemStatusButton>
                                  );
                                })}
                                <GoalStatusDetail>
                                  {accomplishment.openItemsDone}/{accomplishment.openItemsTotal}
                                  {accomplishment.openItemsMet ? ' ✓' : ''}
                                </GoalStatusDetail>
                              </OpenItemsStatusList>
                            ) : (
                              <DataValue></DataValue>
                            )}
                          </DataItem>
                          <DataItem>
                            {renderGoalStepper(member, 'cis', draft)}
                          </DataItem>
                          <DataItem>
                            {renderGoalStepper(member, 'pds', draft)}
                          </DataItem>
                          <DataItem>
                            <OpenItemsCell>
                              {(draft.openItems || ['']).map((item, itemIndex) => (
                                <OpenItemInput
                                  key={`${member.shortcode}-item-${itemIndex}`}
                                  type="text"
                                  value={item}
                                  disabled={!member.doryUserId}
                                  placeholder="Add open item..."
                                  onChange={(e) => updateOpenItemDraft(member.shortcode, itemIndex, e.target.value)}
                                />
                              ))}
                            </OpenItemsCell>
                          </DataItem>
                          <DataItem>
                            <SmallButton
                              type="button"
                              disabled={!member.doryUserId || savingGoals[member.shortcode]}
                              onClick={() => handleSaveGoals(member)}
                            >
                              {savingGoals[member.shortcode] ? 'Saving...' : 'Save'}
                            </SmallButton>
                          </DataItem>
                        </>
                      )}
                    </MemberData>
                    </MemberContent>
                  </MemberRow>
                  );
                })}
                {displayedMembers.length === 0 && (
                  <LoadingMessage>
                    {tableView === 'pace'
                      ? 'No members with check-ins for today found in this group.'
                      : 'No members found in this group.'}
                  </LoadingMessage>
                )}
                </MembersList>
                {displayedMembers.length > 0 && tableView === 'pace' && (
                  <PushScoreEquation>
                    Push Score = (Hours Plan - Optimal Pace) × Pressure Score × (100 - Refresh Rate)
                  </PushScoreEquation>
                )}
              </AccountabilityTableScroll>
                )}
              </>
            )}
          </OverlayContent>
        </OverlayContainer>
      )}
    </>
  );
};

export default GroupsScreen;
